/*!
 * Somnair — shared entrance/scroll animation engine.
 * Upload this file to the theme's assets/ folder, then include it once
 * (e.g. from sm-header.liquid, since the header is always present) with:
 *
 *   <script src="{{ 'somnair-animations.js' | asset_url }}" defer></script>
 *
 * This file only contains generic, purely visual/motion behavior driven by
 * data-attributes shared across every Somnair section — no cart AJAX, no
 * mega menu, no mobile nav, no announcement dismissal. Those stay in their
 * own section files since they're functional logic, not animation. The one
 * exception is the add-to-cart button text feedback below: every section's
 * add-to-cart button already goes through the same native Horizon
 * <add-to-cart-component>/<product-form-component> markup, so it's handled
 * once here by generic selector instead of duplicated per section.
 *
 * Data-attribute contract used across sections:
 *   data-reveal="<ms>"        fade + slide up once, when scrolled into view
 *   data-mask                 hero headline line-mask slide-in (one-shot)
 *   data-countup="<n>"        animate a number up to n once, when in view
 *   data-marquee-track        padded to viewport width, then duplicated once
 *                             to create a seamless loop on any screen size
 *   data-marquee-pause        hovering this host pauses its marquee tracks
 *   data-magnetic + data-sheen  cursor-follow "magnetic" button + sheen sweep
 *   data-card / data-card-bg / data-card-render / data-card-arrow  product card hover tilt
 *   data-zoom / data-zoom-img   image scale-up on card hover
 *   data-orb, data-parallax    scroll- and cursor-driven parallax drift
 *   data-sm-header (element)  gets .is-scrolled after 30px of scroll
 *   data-sm-stickybar + data-threshold  gets .is-visible past the threshold
 */
(function () {
  var reduced = function () { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; };
  var qa = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ---------- scroll reveals ---------- */
  function initReveals() {
    var els = qa('[data-reveal]').filter(function (el) { return !el.dataset.revealBound; });
    if (!els.length) return;
    els.forEach(function (el) { el.dataset.revealBound = '1'; });
    if (reduced()) return;

    /* Set the hidden state with transition:none and force a reflow before
       attaching the real transition below. Above-the-fold elements (e.g.
       the hero) have usually already been painted once in their default
       visible state by the time this script runs, so if a transition were
       active on this very mutation, the "hide" step would itself animate
       — and the reveal, firing a couple of frames later, would interrupt
       that barely-started animation and reverse it, producing a tiny,
       almost invisible flick instead of a real reveal. Committing the
       hidden state instantly first avoids that. */
    els.forEach(function (el) {
      el.style.transition = 'none';
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
    });
    void els[0].offsetHeight;

    var ioFired = false;
    var io = new IntersectionObserver(function (entries) {
      ioFired = true;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.style.transition = 'opacity 0.85s cubic-bezier(0.2,0.7,0.2,1), transform 0.85s cubic-bezier(0.2,0.7,0.2,1)';
        el.style.transitionDelay = (parseInt(el.dataset.reveal, 10) || 0) + 'ms';
        el.style.opacity = '1';
        el.style.transform = 'none';
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    /* Double rAF before observing: guarantees the hidden state above has
       actually been painted at least once before the observer can fire,
       even on a fast/cached load where the observer's first callback can
       land almost immediately. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        els.forEach(function (el) { io.observe(el); });
      });
    });

    /* Fail-safe: if the observer hasn't delivered anything within 700ms for
       whatever reason, reveal everything directly instead of leaving content
       stuck invisible forever. Belt-and-suspenders, not a replacement. */
    setTimeout(function () {
      if (ioFired) return;
      els.forEach(function (el) {
        el.style.transition = 'opacity 0.85s cubic-bezier(0.2,0.7,0.2,1), transform 0.85s cubic-bezier(0.2,0.7,0.2,1)';
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }, 700);
  }

  /* ---------- headline mask reveal (hero h1) ---------- */
  function initHeadline() {
    var masks = qa('[data-mask]').filter(function (el) { return !el.dataset.maskBound; });
    if (!masks.length) return;
    masks.forEach(function (el) { el.dataset.maskBound = '1'; });
    if (reduced()) return;

    /* Same instant-hide-then-transitioned-reveal pattern as initReveals
       above, and for the same reason: the hero h1 is above the fold and
       has almost always already painted its default (visible) look once
       by the time this runs, so attaching the transition before setting
       the hidden position would let the "hide" itself animate — which the
       reveal, firing a couple of frames later, would cut off almost
       immediately and reverse. transition:none + a forced reflow commits
       the hidden position with zero animation, so only the reveal
       (105% -> 0) actually plays. */
    masks.forEach(function (m) {
      m.style.transition = 'none';
      m.style.transform = 'translateY(105%)';
    });
    void masks[0].offsetHeight;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        masks.forEach(function (m, i) {
          m.style.transition = 'transform 1.05s cubic-bezier(0.16,1,0.3,1) ' + (140 + i * 130) + 'ms';
          m.style.transform = 'translateY(0)';
        });
      });
    });
  }

  /* ---------- count-up numbers ---------- */
  function countUp(el) {
    var target = parseFloat(el.dataset.countup || '0');
    if (reduced()) { el.textContent = target.toLocaleString('en-US'); return; }
    var dur = 1500, t0 = performance.now();
    function tick(now) {
      var p = Math.min(1, (now - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * e).toLocaleString('en-US');
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  function initCountUps() {
    qa('[data-countup]').forEach(function (el) {
      if (el.dataset.oneShot) return;
      el.dataset.oneShot = '1';
      if (reduced()) { countUp(el); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { countUp(e.target); io.unobserve(e.target); } });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---------- marquee duplication + hover-pause ---------- */
  function buildMarqueeTrack(track) {
    var uniqueCount = parseInt(track.dataset.marqueeUniqueCount || '0', 10);
    if (!uniqueCount) {
      uniqueCount = track.children.length;
      if (!uniqueCount) return;
      track.dataset.marqueeUniqueCount = String(uniqueCount);
    }

    var viewport = (track.parentElement && track.parentElement.clientWidth) || window.innerWidth;

    /* The CSS loop animates translateX(-50%), which only stays seamless if
       the track holds exactly two identical copies of content. With only a
       handful of unique cards, one copy can be much narrower than a wide or
       ultrawide screen — two skinny copies then leave a real gap partway
       through the loop, no matter how faithfully they're duplicated. Pad a
       single copy first, by cycling the unique cards, until that copy alone
       is at least as wide as the viewport, then duplicate that padded copy
       exactly once so the -50% keyframe still lines up. */
    var copyCount = Math.min(uniqueCount, track.children.length) || uniqueCount;
    var padGuard = 0;
    while (track.scrollWidth < viewport && padGuard < 100) {
      var padSource = track.children[track.children.length % uniqueCount];
      var padClone = padSource.cloneNode(true);
      padClone.setAttribute('aria-hidden', 'true');
      track.appendChild(padClone);
      copyCount = track.children.length;
      padGuard++;
    }
    track.dataset.marqueeCopyCount = String(copyCount);

    /* Self-healing duplication to exactly two copies: always top the track
       back up, no matter what state it's currently in. A one-time "clone
       once, then never touch again" guard can leave a track stuck mid-build
       if an earlier pass gets interrupted (e.g. a theme-editor section
       reload) — that track then runs out of content partway through the CSS
       loop and leaves a visible gap. This converges every time it runs and
       is a no-op once already fully duplicated. */
    var dupGuard = 0;
    while (track.children.length < copyCount * 2 && dupGuard < 200) {
      var source = track.children[track.children.length % copyCount];
      var clone = source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      dupGuard++;
    }
  }

  function initMarquees() {
    qa('[data-marquee-track]').forEach(buildMarqueeTrack);
    qa('[data-marquee-pause]').forEach(function (host) {
      if (host.dataset.bound) return;
      host.dataset.bound = '1';
      var tracks = function () { return qa('[data-marquee-track]', host); };
      host.addEventListener('mouseenter', function () { tracks().forEach(function (t) { t.style.animationPlayState = 'paused'; }); });
      host.addEventListener('mouseleave', function () { tracks().forEach(function (t) { t.style.animationPlayState = 'running'; }); });
    });

    /* Widening the window (or rotating to landscape) can turn a
       previously-wide-enough copy into one that's too narrow again. Bound
       once, globally: on resize, rebuild any track whose single copy no
       longer covers the viewport. Narrowing never needs a rebuild since two
       already-wide-enough copies still cover a smaller viewport. */
    if (!window.__smMarqueeResizeBound) {
      window.__smMarqueeResizeBound = true;
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          qa('[data-marquee-track]').forEach(function (track) {
            var copyCount = parseInt(track.dataset.marqueeCopyCount || '0', 10);
            var uniqueCount = parseInt(track.dataset.marqueeUniqueCount || '0', 10);
            if (!copyCount || !uniqueCount) return;
            var viewport = (track.parentElement && track.parentElement.clientWidth) || window.innerWidth;
            if (track.scrollWidth / 2 < viewport) {
              while (track.children.length > uniqueCount) { track.removeChild(track.lastElementChild); }
              delete track.dataset.marqueeCopyCount;
              buildMarqueeTrack(track);
            }
          });
        }, 200);
      });
    }
  }

  /* ---------- magnetic buttons + sheen ---------- */
  function initMagnetic() {
    qa('[data-magnetic]').forEach(function (el) {
      if (el.dataset.magBound) return;
      el.dataset.magBound = '1';
      var sheen = el.querySelector('[data-sheen]');
      el.addEventListener('mousemove', function (ev) {
        if (reduced()) return;
        var r = el.getBoundingClientRect();
        var dx = (ev.clientX - (r.left + r.width / 2)) / r.width;
        var dy = (ev.clientY - (r.top + r.height / 2)) / r.height;
        el.style.transform = 'translate(' + (dx * 8).toFixed(2) + 'px,' + (dy * 6).toFixed(2) + 'px)';
      });
      el.addEventListener('mouseenter', function () { if (sheen && !reduced()) sheen.style.transform = 'translateX(120%)'; });
      el.addEventListener('mouseleave', function () {
        el.style.transform = 'none';
        if (sheen) {
          sheen.style.transition = 'none';
          sheen.style.transform = 'translateX(-120%)';
          requestAnimationFrame(function () { sheen.style.transition = 'transform 0.7s ease'; });
        }
      });
    });
  }

  /* ---------- product-card hover tilt + image zoom ---------- */
  function initCardHover() {
    qa('[data-card]').forEach(function (card) {
      if (card.dataset.hoverBound) return;
      card.dataset.hoverBound = '1';
      var bg = card.querySelector('[data-card-bg]');
      var render = card.querySelector('[data-card-render]');
      var arrow = card.querySelector('[data-card-arrow]');
      var on = function () {
        if (reduced()) return;
        card.style.transform = 'translateY(-8px)';
        card.style.boxShadow = '0 2px 4px rgba(10,17,34,0.06), 0 44px 70px -34px rgba(10,17,34,0.34)';
        if (bg) bg.style.opacity = '1';
        if (render) render.style.transform = 'rotateX(8deg) rotateY(-11deg) translateZ(22px) scale(1.04)';
        if (arrow) arrow.style.transform = 'translateX(4px)';
      };
      var off = function () {
        card.style.transform = 'none';
        card.style.boxShadow = '';
        if (bg) bg.style.opacity = '0';
        if (render) render.style.transform = 'none';
        if (arrow) arrow.style.transform = 'none';
      };
      card.addEventListener('mouseenter', on);
      card.addEventListener('mouseleave', off);
      card.addEventListener('focusin', on);
      card.addEventListener('focusout', off);
    });
    qa('[data-zoom]').forEach(function (card) {
      if (card.dataset.zoomBound) return;
      card.dataset.zoomBound = '1';
      var img = card.querySelector('[data-zoom-img]');
      card.addEventListener('mouseenter', function () { if (img && !reduced()) img.style.transform = 'scale(1.07)'; });
      card.addEventListener('mouseleave', function () { if (img) img.style.transform = 'none'; });
    });
  }

  /* ---------- scroll-driven: parallax orbs/renders, header shrink, sticky-bar visibility ---------- */
  var scrollBound = false;
  function initScrollEffects() {
    if (scrollBound) return;
    scrollBound = true;
    var header = document.querySelector('[data-sm-header]');
    var raf = null;
    function run() {
      raf = null;
      var y = window.scrollY || 0;
      if (header) header.classList.toggle('is-scrolled', y > 30);

      var bar = document.querySelector('[data-sm-stickybar]');
      if (bar) bar.classList.toggle('is-visible', y > window.innerHeight * (parseFloat(bar.dataset.threshold || '0.7')));

      if (!reduced()) {
        qa('[data-parallax], [data-orb]').forEach(function (el) {
          var f = parseFloat(el.dataset.parallax || el.dataset.orb || '0');
          el.style.translate = '0 ' + (y * f * -1).toFixed(1) + 'px';
        });
      }
    }
    window.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(run); }, { passive: true });
    run();

    if (!reduced()) {
      window.addEventListener('mousemove', function (ev) {
        var cx = (ev.clientX / window.innerWidth - 0.5);
        var cy = (ev.clientY / window.innerHeight - 0.5);
        qa('[data-parallax]').forEach(function (el) {
          var f = parseFloat(el.dataset.parallax || '0') * 260;
          el.style.transform = 'translate3d(' + (cx * f).toFixed(1) + 'px,' + (cy * f * 0.6).toFixed(1) + 'px,0)';
        });
      }, { passive: true });
    }
  }

  /* ---------- add-to-cart button text feedback (Add -> Adding -> Added -> Add) ---------- */
  function findLabelTextNode(btn) {
    for (var i = 0; i < btn.childNodes.length; i++) {
      var node = btn.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim() !== '') return node;
    }
    return null;
  }

  function initAddToCartFeedback() {
    qa('add-to-cart-component button[type="submit"]').forEach(function (btn) {
      if (btn.dataset.atcFeedbackBound) return;
      var textNode = findLabelTextNode(btn);
      if (!textNode) return;
      btn.dataset.atcFeedbackBound = '1';

      var originalText = textNode.textContent;
      var resetTimer = null;

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        clearTimeout(resetTimer);
        textNode.textContent = 'Adding';
        btn.dataset.atcPending = '1';
      });

      document.addEventListener('shopify:cart:lines-update', function () {
        if (btn.dataset.atcPending !== '1') return;
        delete btn.dataset.atcPending;
        textNode.textContent = 'Added';
        resetTimer = setTimeout(function () { textNode.textContent = originalText; }, 3000);
      });

      document.addEventListener('shopify:cart:error', function () {
        if (btn.dataset.atcPending !== '1') return;
        delete btn.dataset.atcPending;
        textNode.textContent = originalText;
      });
    });
  }

  function initAll() {
    initReveals();
    initHeadline();
    initCountUps();
    initMarquees();
    initMagnetic();
    initCardHover();
    initScrollEffects();
    initAddToCartFeedback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
